import React, { useEffect, type JSX } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Container, Title, Text, Grid, Card, ThemeIcon, List, Button, Image, Badge } from '@mantine/core';
import { Link } from 'react-router-dom';
import { IconArrowRight, IconCheck, IconHeartHandshake, IconBrain, IconClockHour4, IconLock, IconWand, IconMoodSmile } from '@tabler/icons-react';

/**
 * Benefits Page - Highlights the advantages of AI emotional support
 * This page is optimized for SEO with keywords related to emotional wellbeing benefits
 */
export function BenefitsPage(): JSX.Element {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* SEO Metadata */}
      <Helmet>
        <title>Benefits of AI Emotional Support | FeelHeard</title>
        <meta name="description" content="Discover the benefits of FeelHeard's AI emotional support: 24/7 availability, personalized reflections, emotional growth tracking, and complete privacy." />
        <meta name="keywords" content="AI emotional support benefits, emotional wellbeing, mental health benefits, digital wellness, emotional intelligence development" />
        <link rel="canonical" href="https://www.feelheard.me/benefits" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.feelheard.me/benefits" />
        <meta property="og:title" content="Benefits of AI Emotional Support | FeelHeard" />
        <meta property="og:description" content="Discover the benefits of FeelHeard's AI emotional support: 24/7 availability, personalized reflections, emotional growth tracking, and complete privacy." />
        <meta property="og:image" content="https://www.feelheard.me/assets/og-image.jpg" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.feelheard.me/benefits" />
        <meta property="twitter:title" content="Benefits of AI Emotional Support | FeelHeard" />
        <meta property="twitter:description" content="Discover the benefits of FeelHeard's AI emotional support: 24/7 availability, personalized reflections, emotional growth tracking, and complete privacy." />
        <meta property="twitter:image" content="https://www.feelheard.me/assets/og-image.jpg" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Benefits of AI Emotional Support | FeelHeard",
            "description": "Discover the benefits of FeelHeard's AI emotional support: 24/7 availability, personalized reflections, emotional growth tracking, and complete privacy.",
            "publisher": {
              "@type": "Organization",
              "name": "FeelHeard",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.feelheard.me/favicon.ico"
              }
            },
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "24/7 Emotional Support",
                  "description": "Access compassionate support anytime, anywhere"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Private & Confidential",
                  "description": "Share without fear of judgment in a secure space"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Emotional Growth Tracking",
                  "description": "Monitor your wellbeing journey over time"
                },
                {
                  "@type": "ListItem",
                  "position": 4,
                  "name": "Personalized Experience",
                  "description": "AI that adapts to your unique communication style"
                }
              ]
            }
          }
        `}</script>
      </Helmet>

      {/* Hero Section */}
      <Box py={80} className="bg-gray-50">
        <Container size="lg">
          <Grid gutter={50} align="center">
            <Grid.Col span={{ base: 6 }}>
              <Box>
                <div className="inline-block bg-[#F4B6A1]/20 px-4 py-2 rounded-full mb-4">
                  <span className="text-[#F4B6A1] font-medium">Emotional Growth & Wellbeing</span>
                </div>
                <Title order={1} className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 font-['Poppins']">
                  The <span className="text-[#C2A4D6]">Benefits</span> of AI Emotional Support
                </Title>
                <Text className="text-lg text-gray-600 mb-8 font-['Merriweather']">
                  Discover how FeelHeard's AI-powered emotional support can enhance your wellbeing, 
                  help process complex feelings, and develop greater emotional intelligence.
                </Text>
                <Button 
                  component={Link} 
                  to="/register"
                  size="lg"
                  radius="xl"
                  className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white px-8 py-4 transition-all transform hover:scale-105"
                >
                  Experience the Benefits
                  <IconArrowRight size={18} style={{ marginLeft: '0.5rem' }} />
                </Button>
              </Box>
            </Grid.Col>
            
            <Grid.Col span={{ base: 6 }}>
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <Badge size="lg" color="pink" variant="light">AI-Powered Support</Badge>
                  <Badge size="lg" color="violet" variant="light">Research-Backed</Badge>
                </div>
                
                <Text className="text-xl font-semibold mb-4">Key Benefits at a Glance:</Text>
                
                <List spacing="md" size="md" center icon={
                  <ThemeIcon color="#A9D0A1" size={24} radius="xl">
                    <IconCheck size={16} />
                  </ThemeIcon>
                }>
                  <List.Item>
                    <Text className="text-gray-700 font-medium">24/7 Emotional Support & Availability</Text>
                  </List.Item>
                  <List.Item>
                    <Text className="text-gray-700 font-medium">Private & Judgment-Free Environment</Text>
                  </List.Item>
                  <List.Item>
                    <Text className="text-gray-700 font-medium">Personalized Reflections & Insights</Text>
                  </List.Item>
                  <List.Item>
                    <Text className="text-gray-700 font-medium">Emotional Growth Tracking & Development</Text>
                  </List.Item>
                  <List.Item>
                    <Text className="text-gray-700 font-medium">Accessible & Affordable Support</Text>
                  </List.Item>
                </List>
              </div>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* Core Benefits Section */}
      <Box py={80}>
        <Container size="lg">
          <Box className="text-center mb-16">
            <Title order={2} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-['Poppins']">
              Core <span className="text-[#B3D9E6]">Benefits</span> of FeelHeard
            </Title>
            <Text className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our AI-powered emotional support platform offers several advantages over traditional approaches, 
              enabling you to develop greater emotional wellbeing and resilience.
            </Text>
          </Box>
          
          <Grid>
            <Grid.Col span={{ md: 4 }}>
              <Card shadow="sm" p="xl" radius="md" withBorder className="h-full transition-all hover:shadow-md">
                <ThemeIcon size={50} radius="md" className="mb-4 bg-[#C2A4D6]/20">
                  <IconClockHour4 size={24} className="text-[#C2A4D6]" />
                </ThemeIcon>
                <Title order={3} className="text-xl font-bold mb-3">24/7 Availability</Title>
                <Text className="text-gray-600">
                  Access emotional support whenever you need it, day or night. No appointments, no waiting. 
                  Cora is always available to listen and respond with thoughtful reflections.
                </Text>
              </Card>
            </Grid.Col>
            
            <Grid.Col span={{ md: 4 }}>
              <Card shadow="sm" p="xl" radius="md" withBorder className="h-full transition-all hover:shadow-md">
                <ThemeIcon size={50} radius="md" className="mb-4 bg-[#F4B6A1]/20">
                  <IconLock size={24} className="text-[#F4B6A1]" />
                </ThemeIcon>
                <Title order={3} className="text-xl font-bold mb-3">Private & Secure</Title>
                <Text className="text-gray-600">
                  Share your feelings in a completely private, judgment-free space. Your conversations are 
                  encrypted and protected, giving you peace of mind and freedom to express yourself.
                </Text>
              </Card>
            </Grid.Col>
            
            <Grid.Col span={{ md: 4 }}>
              <Card shadow="sm" p="xl" radius="md" withBorder className="h-full transition-all hover:shadow-md">
                <ThemeIcon size={50} radius="md" className="mb-4 bg-[#B3D9E6]/20">
                  <IconWand size={24} className="text-[#B3D9E6]" />
                </ThemeIcon>
                <Title order={3} className="text-xl font-bold mb-3">Personalized Experience</Title>
                <Text className="text-gray-600">
                  Our AI learns your communication style and preferences over time, creating an increasingly 
                  personalized experience that resonates with your unique emotional needs.
                </Text>
              </Card>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* Emotional Growth Benefits */}
      <Box py={80} className="bg-gray-50">
        <Container size="lg">
          <Grid gutter={50} align="center">
            <Grid.Col span={{ md: 6 }} order={{ base: 2, md: 1 }}>
              <Box>
                <div className="inline-block bg-[#A9D0A1]/20 px-4 py-2 rounded-full mb-4">
                  <span className="text-[#A9D0A1] font-medium">Emotional Wellbeing</span>
                </div>
                <Title order={2} className="text-3xl font-bold text-gray-800 mb-4 font-['Poppins']">
                  Develop <span className="text-[#A9D0A1]">Emotional Intelligence</span>
                </Title>
                <Text className="text-lg text-gray-600 mb-6">
                  Regular conversations with FeelHeard help you develop greater emotional awareness, understanding, 
                  and regulation — the core components of emotional intelligence.
                </Text>
                
                <List spacing="md" size="lg" className="mb-8">
                  <List.Item icon={
                    <ThemeIcon color="#A9D0A1" size={24} radius="xl">
                      <IconHeartHandshake size={16} />
                    </ThemeIcon>
                  }>
                    <Text className="text-gray-700 font-medium">Improved Emotional Awareness</Text>
                    <Text className="text-gray-600">
                      Identify and name your emotions with greater precision and clarity.
                    </Text>
                  </List.Item>
                  
                  <List.Item icon={
                    <ThemeIcon color="#B3D9E6" size={24} radius="xl">
                      <IconBrain size={16} />
                    </ThemeIcon>
                  }>
                    <Text className="text-gray-700 font-medium">Enhanced Self-Understanding</Text>
                    <Text className="text-gray-600">
                      Recognize patterns in your emotional responses and gain insights into your inner world.
                    </Text>
                  </List.Item>
                  
                  <List.Item icon={
                    <ThemeIcon color="#F4B6A1" size={24} radius="xl">
                      <IconMoodSmile size={16} />
                    </ThemeIcon>
                  }>
                    <Text className="text-gray-700 font-medium">Better Emotional Regulation</Text>
                    <Text className="text-gray-600">
                      Develop strategies to process difficult emotions in healthier, more constructive ways.
                    </Text>
                  </List.Item>
                </List>
              </Box>
            </Grid.Col>
            
            <Grid.Col span={{ md: 6 }} order={{ base: 1, md: 2 }}>
              <Image
                src="https://images.pexels.com/photos/3757942/pexels-photo-3757942.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                alt="Person experiencing emotional growth and wellbeing"
                radius="md"
                className="shadow-xl"
              />
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* Comparison Section */}
      <Box py={80}>
        <Container size="lg">
          <Box className="text-center mb-16">
            <Title order={2} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-['Poppins']">
              How FeelHeard <span className="text-[#C2A4D6]">Compares</span>
            </Title>
            <Text className="text-lg text-gray-600 max-w-3xl mx-auto">
              See how AI emotional support complements other wellbeing approaches to provide a comprehensive solution.
            </Text>
          </Box>
          
          <Box className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-4 text-left border-b-2 border-gray-200"></th>
                  <th className="p-4 text-center border-b-2 border-gray-200 bg-[#C2A4D6]/10">
                    <Text className="text-[#C2A4D6] font-bold">FeelHeard AI</Text>
                  </th>
                  <th className="p-4 text-center border-b-2 border-gray-200">
                    <Text className="text-gray-700">Human Therapy</Text>
                  </th>
                  <th className="p-4 text-center border-b-2 border-gray-200">
                    <Text className="text-gray-700">Self-Help Apps</Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-4 border-b border-gray-200 font-medium">Availability</td>
                  <td className="p-4 text-center border-b border-gray-200 bg-[#C2A4D6]/10">24/7, immediate</td>
                  <td className="p-4 text-center border-b border-gray-200">Scheduled appointments</td>
                  <td className="p-4 text-center border-b border-gray-200">24/7, but limited interaction</td>
                </tr>
                <tr>
                  <td className="p-4 border-b border-gray-200 font-medium">Cost</td>
                  <td className="p-4 text-center border-b border-gray-200 bg-[#C2A4D6]/10">$9.99-19.99/month</td>
                  <td className="p-4 text-center border-b border-gray-200">$100-200+ per session</td>
                  <td className="p-4 text-center border-b border-gray-200">Free to $15/month</td>
                </tr>
                <tr>
                  <td className="p-4 border-b border-gray-200 font-medium">Personalization</td>
                  <td className="p-4 text-center border-b border-gray-200 bg-[#C2A4D6]/10">Adaptive AI learns your style</td>
                  <td className="p-4 text-center border-b border-gray-200">Highly personalized</td>
                  <td className="p-4 text-center border-b border-gray-200">Limited personalization</td>
                </tr>
                <tr>
                  <td className="p-4 border-b border-gray-200 font-medium">Privacy</td>
                  <td className="p-4 text-center border-b border-gray-200 bg-[#C2A4D6]/10">Complete privacy</td>
                  <td className="p-4 text-center border-b border-gray-200">Confidential with some limits</td>
                  <td className="p-4 text-center border-b border-gray-200">Varies by app</td>
                </tr>
                <tr>
                  <td className="p-4 border-b border-gray-200 font-medium">Judgment-Free</td>
                  <td className="p-4 text-center border-b border-gray-200 bg-[#C2A4D6]/10">Complete objectivity</td>
                  <td className="p-4 text-center border-b border-gray-200">Professional but human</td>
                  <td className="p-4 text-center border-b border-gray-200">Mostly objective</td>
                </tr>
              </tbody>
            </table>
          </Box>
          
          <Box mt={20} className="text-center text-gray-600 italic">
            <Text size="sm">
              Note: FeelHeard is designed to complement, not replace, professional mental health services when needed.
            </Text>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box py={80} className="bg-[#F4B6A1]/10">
        <Container size="lg">
          <Box className="text-center mb-16">
            <Title order={2} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-['Poppins']">
              What Our Users <span className="text-[#F4B6A1]">Experience</span>
            </Title>
            <Text className="text-lg text-gray-600 max-w-3xl mx-auto">
              Read about the real benefits people have experienced using FeelHeard's emotional support AI.
            </Text>
          </Box>
          
          <Grid>
            <Grid.Col span={{ md: 4 }}>
              <Card shadow="sm" p="xl" radius="md" withBorder className="h-full">
                <Box className="flex items-center mb-4">
                  <span className="text-[#F4B6A1] text-4xl font-serif mr-2">"</span>
                </Box>
                <Text className="text-gray-700 italic mb-4">
                  FeelHeard has been my emotional anchor during a difficult time. Being able to process my thoughts at 3 AM 
                  when I can't sleep has been invaluable. I've noticed a real improvement in how I handle stress.
                </Text>
                <Box className="mt-auto pt-4 border-t border-gray-100">
                  <Text className="font-bold">Sarah K.</Text>
                  <Text size="sm" className="text-gray-500">Using FeelHeard for 4 months</Text>
                </Box>
              </Card>
            </Grid.Col>
            
            <Grid.Col span={{ md: 4 }}>
              <Card shadow="sm" p="xl" radius="md" withBorder className="h-full">
                <Box className="flex items-center mb-4">
                  <span className="text-[#F4B6A1] text-4xl font-serif mr-2">"</span>
                </Box>
                <Text className="text-gray-700 italic mb-4">
                  As someone who struggles to open up to others, FeelHeard has been perfect. It's helped me identify emotions 
                  I didn't even know I was experiencing. The privacy aspect means I can be completely honest without fear.
                </Text>
                <Box className="mt-auto pt-4 border-t border-gray-100">
                  <Text className="font-bold">Michael T.</Text>
                  <Text size="sm" className="text-gray-500">Using FeelHeard for 7 months</Text>
                </Box>
              </Card>
            </Grid.Col>
            
            <Grid.Col span={{ md: 4 }}>
              <Card shadow="sm" p="xl" radius="md" withBorder className="h-full">
                <Box className="flex items-center mb-4">
                  <span className="text-[#F4B6A1] text-4xl font-serif mr-2">"</span>
                </Box>
                <Text className="text-gray-700 italic mb-4">
                  I use FeelHeard alongside my therapy sessions. It helps me process day-to-day emotions and saves my therapy 
                  time for deeper issues. The voice feature makes it feel like I'm talking to someone who truly cares.
                </Text>
                <Box className="mt-auto pt-4 border-t border-gray-100">
                  <Text className="font-bold">Jamie L.</Text>
                  <Text size="sm" className="text-gray-500">Using FeelHeard for 2 months</Text>
                </Box>
              </Card>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={80}>
        <Container size="md" className="text-center">
          <Title order={2} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-['Poppins']">
            Start Your <span className="text-[#C2A4D6]">Emotional Wellbeing</span> Journey
          </Title>
          <Text className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            Experience the benefits of AI emotional support firsthand with our 7-day free trial.
            No commitment required – see how FeelHeard can enhance your emotional wellbeing.
          </Text>
          
          <Grid>
            <Grid.Col span={{ base: 6 }}>
              <Card shadow="sm" p="xl" radius="md" withBorder className="h-full bg-[#C2A4D6]/5">
                <Title order={3} className="text-xl font-bold mb-3">Text Companion</Title>
                <Text className="text-3xl font-bold mb-2">${"9.99"}<span className="text-sm text-gray-500">/month</span></Text>
                <Text className="text-gray-600 mb-4">Essential emotional support through text conversations</Text>
                
                <Button 
                  component={Link} 
                  to="/register"
                  radius="xl"
                  className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white w-full py-2 transition-all"
                >
                  Start 7-Day Free Trial
                </Button>
              </Card>
            </Grid.Col>
            
            <Grid.Col span={{ base: 6 }}>
              <Card shadow="sm" p="xl" radius="md" withBorder className="h-full bg-[#B3D9E6]/5">
                <Title order={3} className="text-xl font-bold mb-3">Voice Connect</Title>
                <Text className="text-3xl font-bold mb-2">${"19.99"}<span className="text-sm text-gray-500">/month</span></Text>
                <Text className="text-gray-600 mb-4">Enhanced support with voice conversations and premium features</Text>
                
                <Button 
                  component={Link} 
                  to="/register"
                  radius="xl"
                  className="bg-[#B3D9E6] hover:bg-[#9FCFDF] text-white w-full py-2 transition-all"
                >
                  Start 7-Day Free Trial
                </Button>
              </Card>
            </Grid.Col>
          </Grid>
          
          <Text className="text-gray-500 mt-4">
            No credit card required for trial. Cancel anytime.
          </Text>
        </Container>
      </Box>
    </>
  );
}

export default BenefitsPage;